import { BarSegment, useChart } from "@chakra-ui/charts";
import { Box, Card, Flex, IconButton, Text } from "@chakra-ui/react";
import { Tooltip as ChakraTooltip } from "@/components/ui/tooltip";
import { MdInfoOutline } from "react-icons/md";

type GrowthZoneCellProps = {
  fragments_count_0_25: number;
  fragments_count_25_50: number;
  fragments_count_50_75: number;
  fragments_count_75_100: number;
};

const GrowthZoneCell = ({
  fragments_count_0_25,
  fragments_count_25_50,
  fragments_count_50_75,
  fragments_count_75_100,
}: GrowthZoneCellProps) => {
  const chart = useChart({
    data: [
      { name: "1/4", value: fragments_count_0_25, color: "blue.100" },
      { name: "1/2", value: fragments_count_25_50, color: "blue.300" },
      { name: "3/4", value: fragments_count_50_75, color: "blue.500" },
      { name: "Full", value: fragments_count_75_100, color: "blue.700" },
    ].filter((item) => item.value > 0),
  });

  return (
    <BarSegment.Root chart={chart} barSize="4">
      <BarSegment.Content>
        <BarSegment.Bar
          css={{
            "& > div": {
              borderRadius: 0,
              "&:first-of-type": { borderLeftRadius: "full" },
              "&:last-child": { borderRightRadius: "full" },
            },
          }}
          gap="0"
          tooltip
        />
      </BarSegment.Content>
    </BarSegment.Root>
  );
};

type GrowthZoneBoxProps = {
  title: string;
  fragments_count_0_25: number;
  fragments_count_25_50: number;
  fragments_count_50_75: number;
  fragments_count_75_100: number;
  outplantingDate?: string | null;
};

const GrowthZoneBox = ({
  title,
  fragments_count_0_25,
  fragments_count_25_50,
  fragments_count_50_75,
  fragments_count_75_100,
  outplantingDate,
}: GrowthZoneBoxProps) => {
  const chartData = [
    { name: "1/4", value: fragments_count_0_25, color: "blue.100" },
    { name: "1/2", value: fragments_count_25_50, color: "blue.300" },
    { name: "3/4", value: fragments_count_50_75, color: "blue.500" },
    { name: "Full", value: fragments_count_75_100, color: "blue.700" },
  ];

  const chart = useChart({
    data: chartData.filter((item) => item.value > 0),
  });

  const largestZone = chartData.reduce(
    (max, item) => (item.value > max.value ? item : max),
    chartData[0],
  );
  const multipleLargestZones = chartData.filter(
    (item) => item.value === largestZone.value,
  );
  const totalFragments = chartData.reduce(
    (acc, curr) => (acc += curr.value),
    0,
  );

  return (
    <Card.Root size="sm">
      <Card.Body>
        <Flex justifyContent="space-between" alignItems="center" mb="6">
          <Text textAlign="left" fontWeight="600">
            {title}
          </Text>
          <ChakraTooltip
            content="Distribution of fragments by growth zone."
            contentProps={{ color: "white" }}
          >
            <IconButton variant="ghost" size="xs" opacity="0.5">
              <MdInfoOutline />
            </IconButton>
          </ChakraTooltip>
        </Flex>
        <Box>
          <BarSegment.Root chart={chart} barSize="8">
            <BarSegment.Content color="gray.400">
              <BarSegment.Bar
                css={{
                  "& > div": {
                    borderRadius: 0,
                    "&:first-of-type": { borderLeftRadius: "sm" },
                    "&:last-child": { borderRightRadius: "sm" },
                  },
                }}
                gap="0"
                tooltip
              />
              <BarSegment.Legend
                fontSize="xs"
                showPercent
                css={{
                  "& > div": {
                    gap: "4px"
                  },
                  "& > div > div": {
                    gap: "4px"
                  }
                }}
              />
            </BarSegment.Content>
          </BarSegment.Root>
        </Box>
        <Box textAlign="left" mt={5}>
          {largestZone.value > 0 && (
            <Text
              fontSize="sm"
              color="gray.900"
              fontWeight="600"
              display="inline"
            >
              {multipleLargestZones.length === 2
                ? `Most fragments at ${multipleLargestZones.map((i) => i.name).join(" and ")} sizes.`
                : `Most fragments at ${largestZone.name} size.`}
            </Text>
          )}
          {fragments_count_75_100 > 0 && (
            <Text fontSize="xs" opacity="0.8">
              {" "}
              {Math.round((fragments_count_75_100 / totalFragments) * 100)}% ready
              for outplanting.
            </Text>
          )}
          {outplantingDate && (
            <Text fontSize="xs" opacity="0.6">At current growth, ready to outplant ~ {outplantingDate}.</Text>
          )}
        </Box>
      </Card.Body>
    </Card.Root>
  );
};

export { GrowthZoneCell, GrowthZoneBox };
